import React from 'react';
import { Book, Code, Terminal, Database, Shield, Zap } from 'lucide-react';

function Documentation() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1920&h=1080"
              alt="Documentation hero"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">Documentation</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              Comprehensive guides and technical documentation for Atlas AI's services and integrations.
            </p>
          </div>
        </div>
      </section>

      {/* Main Documentation Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <DocSection
            icon={<Terminal className="h-8 w-8 text-[#00ffff]" />}
            title="Getting Started"
            description="Quick start guides and basic concepts to help you begin with Atlas AI."
            links={[
              "Platform Overview",
              "Account Setup",
              "Basic Concepts",
              "First Steps"
            ]}
          />

          <DocSection
            icon={<Code className="h-8 w-8 text-[#00ffff]" />}
            title="API Reference"
            description="Detailed API documentation for integrating Atlas AI services."
            links={[
              "Authentication",
              "Endpoints",
              "Rate Limits",
              "Error Handling"
            ]}
          />

          <DocSection
            icon={<Database className="h-8 w-8 text-[#00ffff]" />}
            title="Data Management"
            description="Learn about data handling, storage, and processing capabilities."
            links={[
              "Data Models",
              "Storage Options",
              "Backup & Recovery",
              "Data Migration"
            ]}
          />

          <DocSection
            icon={<Shield className="h-8 w-8 text-[#00ffff]" />}
            title="Security"
            description="Security best practices and implementation guidelines."
            links={[
              "Security Overview",
              "Access Control",
              "Encryption",
              "Compliance"
            ]}
          />

          <DocSection
            icon={<Book className="h-8 w-8 text-[#00ffff]" />}
            title="Tutorials"
            description="Step-by-step guides for common implementation scenarios."
            links={[
              "Basic Integration",
              "Advanced Features",
              "Best Practices",
              "Common Use Cases"
            ]}
          />

          <DocSection
            icon={<Zap className="h-8 w-8 text-[#00ffff]" />}
            title="SDKs & Tools"
            description="Official SDKs and development tools for major platforms."
            links={[
              "JavaScript SDK",
              "Python SDK",
              "CLI Tools",
              "Development Tools"
            ]}
          />
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="white-holographic-card p-12 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Search Documentation</h2>
          <div className="max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search documentation..."
              className="w-full px-6 py-4 rounded-lg border-2 border-[#00ffff] focus:outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function DocSection({ icon, title, description, links }) {
  return (
    <div className="white-holographic-card p-8 rounded-xl">
      <div className="mb-6">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-800 mb-3">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      <ul className="space-y-2">
        {links.map((link, index) => (
          <li key={index}>
            <a href="#" className="text-[#00ffff] hover:text-[#ff0000] transition-colors">
              {link}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Documentation;